import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, Trash2, X, GraduationCap, FileText, ArrowRight, ShieldCheck, Paperclip } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const emptyModule = { title: "", description: "", content: "", duration_minutes: 30, category: "General" };
const CATEGORIES = ["General", "Asbestos", "Fire Safety", "Water Hygiene", "Damp & Mould", "Compliance"];

export default function AdminTraining() {
  const { user } = useAuth();
  const [modules, setModules] = useState(null);
  const [completions, setCompletions] = useState([]);
  const [creating, setCreating] = useState(null);
  const [saving, setSaving] = useState(false);
  const [staffList, setStaffList] = useState(null);
  const [tab, setTab] = useState("modules"); // modules | records

  const load = () => {
    api.get("/training/modules").then(r => setModules(r.data));
    api.get("/training/completions").then(r => setCompletions(r.data)).catch(() => setCompletions([]));
    if (user?.role === "admin" || user?.role === "manager") loadStaff();
  };
  const loadStaff = async () => {
    try {
      const usersRes = await api.get("/users");
      const list = usersRes.data;
      // Fetch each user's records in parallel; gracefully degrade if any fails
      const enriched = await Promise.all(list.map(async (u) => {
        try {
          const r = await api.get(`/staff/${u.id}/records`);
          return { ...u, _qual: r.data.qualifications.length, _train: r.data.training.length, _obs: r.data.observations.length, _auth: r.data.authorisations.length };
        } catch { return { ...u, _qual: 0, _train: 0, _obs: 0, _auth: 0 }; }
      }));
      setStaffList(enriched);
    } catch { setStaffList([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/training/modules", creating);
      toast.success("Module created");
      setCreating(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete module and all its completions?")) return;
    try {
      await api.delete(`/training/modules/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const isAdmin = user?.role === "admin";
  const canManageStaff = isAdmin || user?.role === "manager";

  return (
    <div className="p-8 lg:p-10" data-testid="page-admin-training">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Learning &amp; competence</p>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900">Staff training</h1>
          <p className="text-slate-500 mt-2">CPD modules and per-staff training records, certificates and competence authorisations.</p>
        </div>
        {tab === "modules" && isAdmin && (
          <button onClick={() => setCreating(emptyModule)} className="gs-gradient text-white font-semibold px-5 py-2.5 flex items-center gap-2 hover:opacity-90" data-testid="new-module-btn">
            <Plus className="w-4 h-4" /> New module
          </button>
        )}
      </div>

      {canManageStaff && (
        <div className="flex gap-1 mb-6 border-b border-slate-200">
          {[
            { v: "modules", label: "CPD modules", icon: GraduationCap },
            { v: "records", label: "Staff records &amp; certs", icon: ShieldCheck },
          ].map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)} className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px flex items-center gap-2 ${tab === t.v ? "border-[#3FBAB0] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`} data-testid={`tab-${t.v}`}>
              <t.icon className="w-4 h-4" /> <span dangerouslySetInnerHTML={{ __html: t.label }} />
            </button>
          ))}
        </div>
      )}

      {tab === "modules" && (
        modules === null ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
              {modules.map(m => (
                <div key={m.id} className="bg-white border border-slate-200 p-6 flex flex-col" data-testid={`module-card-${m.id}`}>
                  <div className="flex justify-between items-start mb-4">
                    <GraduationCap className="w-6 h-6 text-[#1FA59A]" strokeWidth={1.5} />
                    {isAdmin && <button onClick={() => remove(m.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-module-${m.id}`}><Trash2 className="w-4 h-4" /></button>}
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">{m.category} · {m.duration_minutes} min</p>
                  <h3 className="font-display text-xl font-semibold text-slate-900 mb-3">{m.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed flex-1">{m.description}</p>
                </div>
              ))}
              {modules.length === 0 && <p className="text-slate-500">No modules yet.</p>}
            </div>

            {isAdmin && (
              <div className="bg-white border border-slate-200">
                <div className="p-6 border-b border-slate-200">
                  <h2 className="font-display text-xl font-semibold text-slate-900">Completion log</h2>
                  <p className="text-sm text-slate-500 mt-1">All staff training completions across modules.</p>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
                      <th className="px-6 py-3 font-medium">Staff</th>
                      <th className="px-6 py-3 font-medium">Module</th>
                      <th className="px-6 py-3 font-medium">Score</th>
                      <th className="px-6 py-3 font-medium">Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completions.length === 0 && <tr><td colSpan={4} className="px-6 py-10 text-center text-slate-400">No completions yet.</td></tr>}
                    {completions.map(c => (
                      <tr key={c.id} className="border-t border-slate-100" data-testid={`completion-${c.id}`}>
                        <td className="px-6 py-4"><p className="font-medium">{c.user_name}</p><p className="text-xs text-slate-500">{c.user_email}</p></td>
                        <td className="px-6 py-4 text-slate-600">{c.module_title}</td>
                        <td className="px-6 py-4"><span className="font-semibold text-emerald-700">{c.score}%</span></td>
                        <td className="px-6 py-4 text-slate-500">{new Date(c.completed_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )
      )}

      {tab === "records" && canManageStaff && (
        <div className="bg-white border border-slate-200">
          <div className="p-6 border-b border-slate-200 flex items-start justify-between gap-4">
            <div>
              <h2 className="font-display text-xl font-semibold text-slate-900">Per-staff training, certs &amp; authorisations</h2>
              <p className="text-sm text-slate-500 mt-1">Click any staff member to add training, upload certificates, log observation audits and sign off competency authorisations.</p>
            </div>
          </div>
          {staffList === null ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : staffList.length === 0 ? (
            <div className="py-10 text-center text-slate-400 text-sm">No staff users yet — add one from <Link to="/admin/users" className="text-[#1FA59A] underline">Users</Link>.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
                  <th className="px-6 py-3 font-medium">Staff member</th>
                  <th className="px-6 py-3 font-medium">Role</th>
                  <th className="px-6 py-3 font-medium">Qualifications</th>
                  <th className="px-6 py-3 font-medium">Training</th>
                  <th className="px-6 py-3 font-medium">Observations</th>
                  <th className="px-6 py-3 font-medium">Authorisations</th>
                  <th className="px-6 py-3 font-medium w-32"></th>
                </tr>
              </thead>
              <tbody>
                {staffList.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`training-staff-row-${s.id}`}>
                    <td className="px-6 py-4">
                      <p className="font-medium text-slate-900">{s.name}</p>
                      <p className="text-xs text-slate-500">{s.email}{s.job_title ? ` · ${s.job_title}` : ""}</p>
                    </td>
                    <td className="px-6 py-4 text-xs capitalize text-slate-600">{s.role}</td>
                    <td className="px-6 py-4 text-slate-700"><Paperclip className="w-3 h-3 inline mr-1 text-slate-400" />{s._qual}</td>
                    <td className="px-6 py-4 text-slate-700">{s._train}</td>
                    <td className="px-6 py-4 text-slate-700">{s._obs}</td>
                    <td className="px-6 py-4 text-slate-700">{s._auth}</td>
                    <td className="px-6 py-4">
                      <Link to={`/admin/staff/${s.id}`} className="text-[#1FA59A] font-semibold text-xs hover:underline flex items-center gap-1" data-testid={`manage-staff-${s.id}`}>
                        Manage <ArrowRight className="w-3 h-3" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => setCreating(null)} data-testid="new-module-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white w-full max-w-xl">
            <div className="p-6 border-b border-slate-200 flex justify-between items-start">
              <h2 className="font-display text-2xl font-bold">New training module</h2>
              <button type="button" onClick={() => setCreating(null)} className="text-slate-400 hover:text-slate-900"><X /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Title</label>
                <input required value={creating.title} onChange={(e) => setCreating({ ...creating, title: e.target.value })} className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#1FA59A]" data-testid="module-title" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Category</label>
                  <select value={creating.category} onChange={(e) => setCreating({ ...creating, category: e.target.value })} className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#1FA59A]" data-testid="module-category">
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div><label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Duration (min)</label>
                  <input required type="number" min={5} value={creating.duration_minutes} onChange={(e) => setCreating({ ...creating, duration_minutes: parseInt(e.target.value) })} className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#1FA59A]" data-testid="module-duration" />
                </div>
              </div>
              <div><label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Description</label>
                <textarea required rows={3} value={creating.description} onChange={(e) => setCreating({ ...creating, description: e.target.value })} className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#1FA59A]" data-testid="module-description" />
              </div>
              <div><label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Content (HTML)</label>
                <textarea required rows={8} value={creating.content} onChange={(e) => setCreating({ ...creating, content: e.target.value })} className="w-full border border-slate-300 px-4 py-3 font-mono text-xs focus:outline-none focus:border-[#1FA59A]" data-testid="module-content" />
              </div>
            </div>
            <div className="p-6 border-t border-slate-200">
              <button type="submit" disabled={saving} className="gs-gradient text-white font-semibold px-6 py-3 w-full hover:opacity-90 disabled:opacity-50" data-testid="module-save">
                {saving ? "Saving…" : "Create module"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
